// modelsApi.ts
import { baseApi } from './base-api';
import { API_ENDPOINTS } from '../utils/constants';

export interface RuleItem {
  id: string;
  title: string;
  body: string;
  enabled?: boolean;
}

export interface DictionaryItem {
  id: string;
  term: string;
  meaning: string;
}

export interface ModelPersona {
  answer_length?: string;
  tone_of_voice?: string;
  use_emojis?: boolean;
  use_markdown_links?: boolean;
  communication_styles?: RuleItem[];
}

export interface ModelRules {
  context_clarification?: RuleItem[];
  spam?: RuleItem[];
  other?: RuleItem[];
  knowledge_gap?: RuleItem[];
}

export interface ModelPromptMigrationResponse {
  settings: {
    metadata?: Record<string, any>;
    persona?: ModelPersona;
    rules?: ModelRules;
    dictionary?: DictionaryItem[];
  };
  unmapped: Array<{ section: string; content: string; reason: string }>;
  notes: string[];
  coverage?: Array<{
    source_id: string;
    status: 'migrated' | 'covered_by_base' | 'unmapped';
    destination?: string | null;
    reason?: string | null;
  }>;
  comparison_prompt: string;
  proposed_prompt: string;
  validated: boolean;
}

export interface ModelPromptPreviewResponse {
  prompt: string;
  route: ModelPromptPreviewRoute;
  use_new_prompt: boolean;
}

export type ModelPromptPreviewRoute = 'action' | 'fresh_rag' | 'direct_context' | 'clarification' | 'reuse_evidence';

export interface StandardResponse {
  id: string;
  label: string;
  value: string;
}

export interface ResponseGuidanceTemplate {
  id: string;
  question: string;
  answer: string;
  active?: boolean;
}

export interface SetupSnapshot {
  source?: string;
  website_knowledge?: string;
  document_knowledge?: string;
  agent_limitations?: string;
  tone_of_voice?: string;
  use_emojis?: boolean;
  chat_button?: string;
  start_message?: string;
  placement?: string;
  agent_name?: string;
  display_name?: string;
  brand_color?: string;
  has_logo?: boolean;
  anchor_label_text?: string;
  custom_start_message?: string;
  fallback_is_contact_form?: boolean;
  fallback_mode?: 'contact_form' | 'direct_contact' | 'forward';
  direct_contact_details?: string;
  support_email?: string;
  suggested_queries_count?: number;
  connected_inbox_accounts?: string[];
  assigned_inbox_accounts?: string[];
  inbox_accounts?: string[];
  email_signature?: string;
  setup_comment?: string;
}

export type ModelType = 'chat' | 'voice' | 'search' | 'inbox';

export type InboxMode = 'email' | 'ticketing' | 'messaging';

export type InboxIntegrationSettingsProvider = 'gmail' | 'outlook' | 'zendesk' | 'gorgias' | 'dixa';

export interface InboxIntegrationSettingsSnapshot {
  schema_version: 1;
  provider: InboxIntegrationSettingsProvider;
  target: {
    account_id: string;
    account_email?: string;
    mailbox_email?: string;
  };
  settings: Record<string, any>;
}

export interface Model {
  id: string;
  type?: ModelType;
  inbox_mode?: InboxMode | null;
  name?: string;
  status?: 'active' | 'deactivated' | 'archived';
  datastreams?: string[];
  metadata?: Record<string, any> | null;
  voice?: Record<string, any> | null;
  voice_model?: { model_id: string } | null;
  use_new_prompt?: boolean;
  persona?: ModelPersona | null;
  rules?: ModelRules | null;
  standard_responses?: StandardResponse[];
  response_guidance_templates?: ResponseGuidanceTemplate[];
  published_version?: string | null;
  webmcp_enabled?: boolean;
  skip_emoji_only_messages?: boolean;
  inbox_integration_settings?: InboxIntegrationSettingsSnapshot | null;
  revision_id?: string | null;
  setup_completed?: boolean;
  setup_completed_at?: string | null;
  setup_skipped?: boolean;
  setup_skipped_at?: string | null;
  setup_snapshot?: SetupSnapshot | null;
  updated?: string;
  _setup_website_knowledge?: string;
  _setup_document_knowledge?: string;
  _setup_agent_limitations?: string;
  _setup_chat_button?: string;
  _setup_start_message?: string;
  _setup_placement?: string;
  setup_comment?: string;
  llm?: ModelLLMSettings | null;
  prompts?: Record<string, any> | null;
  styles?: Record<string, any> | null;
  actions?: Record<string, any> | null;
  display?: Record<string, any> | null;
  responder?: Record<string, any> | null;
  group?: Record<string, any> | null;
  mcp_servers?: Record<string, any>[];
  dictionary?: DictionaryItem[];
  // ... other model properties
}

export interface ModelResponse {
  type: 'success';
  response: Model;
}

export interface ModelLLMSettings extends Record<string, any> {
  use_rerank?: boolean;
}

/** Configuration fields only; resource identity and version metadata are server-owned. */
export interface ModelConfigurationUpdate {
  name?: string;
  metadata?: Record<string, any>;
  styles?: Record<string, any>;
  llm?: ModelLLMSettings | null;
  prompts?: Record<string, any>;
  datastreams?: string[];
  actions?: Record<string, any>;
  group?: Record<string, any>;
  voice?: Record<string, any>;
  voice_model?: { model_id: string } | null;
  display?: Record<string, any>;
  rating?: Record<string, any>;
  responder?: Record<string, any>;
  popup_messages?: any[];
  suggested_queries?: any[];
  re_engage?: Record<string, any>;
  tracking?: Record<string, any>;
  privacy?: Record<string, any>;
  support?: Record<string, any>;
  persona?: ModelPersona;
  rules?: ModelRules;
  standard_responses?: StandardResponse[];
  response_guidance_templates?: ResponseGuidanceTemplate[];
  dictionary?: DictionaryItem[];
  skip_emoji_only_messages?: boolean;
  inbox_integration_settings?: InboxIntegrationSettingsSnapshot | null;
  mcp_servers?: Record<string, any>[];
  use_new_prompt?: boolean;
  insights_categories?: any[];
  custom_insights_categories?: any[];
  fallback_insights_category?: string | null;
  digit_restriction?: Record<string, any>;
  setup_completed?: boolean;
  setup_completed_at?: string;
  setup_skipped?: boolean;
  setup_skipped_at?: string;
  setup_snapshot?: SetupSnapshot;
  setup_comment?: string;
}

export type ModelUpdate = ModelConfigurationUpdate | { published_version: string } | { status: 'active' | 'deactivated' | 'archived' };
export interface ModelUpdateResponse {
  type: 'success';
  response: { revision_id?: string };
}

interface ModelVersion {
  id: string;
  version_id?: string;
  created: string;
  type?: Model['type'];
  inbox_mode?: Model['inbox_mode'];
  status?: string;
  voice?: Record<string, any> | null;
  voice_model?: { model_id: string } | null;
  response_guidance_templates?: ResponseGuidanceTemplate[];
  skip_emoji_only_messages?: boolean;
  inbox_integration_settings?: InboxIntegrationSettingsSnapshot | null;
  [key: string]: any;
}

// Selectable API fields represented by Model, excluding revision and UI draft fields.
export type ModelSelectField =
  | 'id'
  | 'type'
  | 'inbox_mode'
  | 'name'
  | 'status'
  | 'datastreams'
  | 'metadata'
  | 'voice'
  | 'voice_model'
  | 'use_new_prompt'
  | 'persona'
  | 'rules'
  | 'standard_responses'
  | 'response_guidance_templates'
  | 'webmcp_enabled'
  | 'skip_emoji_only_messages'
  | 'inbox_integration_settings'
  | 'published_version'
  | 'setup_completed'
  | 'setup_completed_at'
  | 'setup_skipped'
  | 'setup_skipped_at'
  | 'setup_snapshot'
  | 'updated'
  | 'setup_comment';

export interface GetModelsArgs {
  uid?: string;
  org_id?: string;
  filters?: Record<string, string | number>; // optional map of query parameters
  select?: ModelSelectField[];
}

export interface GetModelsResponse {
  type: string;
  response: {
    list: Model[];
    total: number;
  };
}

export interface ConnectInboxArgs {
  model_id: string;
  value: string;
  label: string;
}

export interface CreateModelArgs extends ModelConfigurationUpdate {
  type: ModelType;
  inbox_mode?: InboxMode;
}

interface DuplicateModelArgs {
  id: string;
}

interface ActionFlowTemplate {
  id: string;
  label: string;
  description: string;
  configurable_params: string[];
  graph: {
    nodes: any[];
    edges: any[];
  };
}

interface GetActionFlowTemplatesResponse {
  type: string;
  response: ActionFlowTemplate[];
}

interface GetNodeTemplatesResponse {
  type: string;
  response: any[];
}

const modelsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getModels: builder.query<GetModelsResponse, GetModelsArgs>({
      query: ({ uid, org_id, filters, select }: GetModelsArgs) => {
        // Build the URL with query parameters
        const url = new URL('/v1/models', API_ENDPOINTS.KARLA_FLASK_API);
        const params: Record<string, string> = {};

        if (uid) params['uid'] = uid;
        if (org_id) params['org_id'] = org_id;

        // Append parameters to the URL (if any filters provided)
        if (filters) {
          Object.keys(filters).forEach((key) => url.searchParams.append(key, String(filters[key])));
        }

        if (select?.length) url.searchParams.set('select', select.join(','));

        // Append parameters to the URL
        Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: ['Model'],
    }),
    getModelById: builder.query<ModelResponse, string>({
      query: (id) => {
        // Build the URL with query parameters
        const url = new URL(`/v1/models/${id}`, API_ENDPOINTS.KARLA_FLASK_API);
        const params: Record<string, string> = {};

        // Append parameters to the URL
        Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));

        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: (result, error, id) => [{ type: 'Model', id }],
    }),
    migrateModelPrompt: builder.mutation<{ type: string; response: ModelPromptMigrationResponse }, { id: string; system_prompt?: string }>({
      query: ({ id, system_prompt }) => ({
        url: `models/${id}/migrate-prompt`,
        method: 'POST',
        body: system_prompt === undefined ? {} : { system_prompt },
      }),
    }),
    getModelPromptPreview: builder.mutation<{ type: string; response: ModelPromptPreviewResponse }, { id: string; draft?: Record<string, any>; route?: ModelPromptPreviewRoute }>({
      query: ({ id, draft, route }) => ({
        url: `models/${id}/prompt-preview`,
        method: 'POST',
        body: {
          ...(draft === undefined ? {} : { draft }),
          ...(route === undefined ? {} : { route }),
        },
      }),
    }),
    connectInbox: builder.mutation<void, ConnectInboxArgs>({
      query: (payload) => ({
        url: '/hubspot/connect/account',
        method: 'POST',
        body: payload,
      }),
    }),
    updateModel: builder.mutation<ModelUpdateResponse, { id: string; updatedData: ModelUpdate; createRevision?: boolean; skipCacheInvalidation?: boolean }>({
      query: ({ id, updatedData, createRevision }) => {
        return {
          url: `models/${id}`,
          method: 'PATCH',
          body: { ...updatedData, ...(createRevision ? { _create_revision: true } : {}) },
        };
      },
      invalidatesTags: (result, error, { id, updatedData }) => {
        const lifecycleChanged = ('status' in updatedData && updatedData.status === 'archived') || 'published_version' in updatedData;
        return error && !lifecycleChanged ? [] : [{ type: 'Model', id }, ...(lifecycleChanged ? ['Integrations' as const] : [])];
      },
    }),
    revealActionSecret: builder.mutation<{ value: string }, { model_id: string; node_id: string; secret_type: 'header' | 'api_key' | 'param'; header_index?: number; param_id?: string }>({
      query: ({ model_id, ...body }) => ({
        url: new URL(`/v1/models/${model_id}/action-secrets/reveal`, API_ENDPOINTS.KARLA_FLASK_API).toString(),
        method: 'POST',
        body,
      }),
      transformResponse: (response: { value?: string; secret?: string; response?: { value?: string; secret?: string } }) => ({ value: response.response?.value ?? response.response?.secret ?? response.value ?? response.secret ?? '' }),
    }),
    createModel: builder.mutation<ModelResponse, CreateModelArgs>({
      query: (data) => ({
        url: '/models',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Models', 'Model'],
    }),
    updateModelsStatus: builder.mutation<void, { ids: string[]; status: 'active' | 'deactivated' | 'archived' }>({
      query: ({ ids, status }) => ({
        url: 'models/status',
        method: 'PATCH',
        body: { ids, status },
      }),
      invalidatesTags: (result, error, { ids, status }) => ['Model', ...ids.map((id) => ({ type: 'Model' as const, id })), ...(status === 'archived' ? ['Integrations' as const] : [])],
    }),
    deleteArchivedModel: builder.mutation<void, string>({
      query: (id) => ({
        url: `models/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['Model', { type: 'Model', id }],
    }),
    deleteArchivedModels: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: 'models/bulk-delete',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: (result, error, ids) => ['Model', ...ids.map((id) => ({ type: 'Model' as const, id }))],
    }),
    getModelVersion: builder.query<ModelVersion, { modelId: string; versionId: string }>({
      query: ({ modelId, versionId }) => {
        const url = new URL(`/models/${modelId}/versions/${versionId}`, API_ENDPOINTS.KARLA_FLASK_API);

        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      transformResponse: (response: { model: ModelVersion }) => response.model,
      providesTags: (result, error, { modelId }) =>
        result
          ? [
              { type: 'ModelVersion', id: modelId },
              { type: 'ModelVersion', id: result.version_id },
            ]
          : [{ type: 'ModelVersion', id: modelId }],
    }),

    // 2) fetch all versions for a model
    getAllModelVersions: builder.query<ModelVersion[], string>({
      query: (modelId) => ({
        url: `/models/${modelId}/versions`,
        baseUrl: API_ENDPOINTS.KARLA_FLASK_API,
        method: 'GET',
      }),
      // the endpoint returns an array of versions directly
      providesTags: (result, error, modelId) => (result ? [{ type: 'ModelVersion', id: modelId }, ...result.map((v) => ({ type: 'ModelVersion' as const, id: v.version_id }))] : [{ type: 'ModelVersion', id: modelId }]),
    }),
    addModelVersion: builder.mutation<{ type: string; response: string }, { modelId: string; revisionId?: string }>({
      query: ({ modelId, revisionId }) => ({
        url: `/models/${modelId}/versions`,
        baseUrl: API_ENDPOINTS.KARLA_FLASK_API,
        method: 'POST',
        ...(revisionId ? { body: { revision_id: revisionId } } : {}),
      }),
      invalidatesTags: (_res, _err, { modelId }) => [
        { type: 'Model' as const, id: modelId },
        { type: 'ModelVersion' as const, id: modelId },
      ],
    }),
    discardModelRevisions: builder.mutation<{ type: string; response: { discarded_revisions: number } }, { modelId: string }>({
      query: ({ modelId }) => ({
        url: `/models/${modelId}/revisions`,
        baseUrl: API_ENDPOINTS.KARLA_FLASK_API,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { modelId }) => [{ type: 'Model' as const, id: modelId }],
    }),
    duplicateModel: builder.mutation<Model, DuplicateModelArgs>({
      query: (data) => ({
        url: `/models/${data.id}/duplicate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Model'],
    }),
    trainModel: builder.mutation<void, { manual: boolean; ids: any[] }>({
      query: ({ manual, ids }) => ({
        url: `/models/train`,
        method: 'POST',
        body: { manually_triggered_embedding: manual, ids: ids },
      }),
      invalidatesTags: [{ type: 'Model', id: 'LIST' }],
    }),
    getActionFlowTemplates: builder.query<GetActionFlowTemplatesResponse, void>({
      query: () => ({
        url: 'action-flow-templates',
        method: 'GET',
      }),
    }),
    getNodeTemplates: builder.query<GetNodeTemplatesResponse, 'action' | 'trigger'>({
      query: (type) => ({
        url: `node-templates?type=${type}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useConnectInboxMutation,
  useTrainModelMutation,
  useUpdateModelsStatusMutation,
  useDeleteArchivedModelMutation,
  useDeleteArchivedModelsMutation,
  useGetModelsQuery,
  useGetModelByIdQuery,
  useGetModelPromptPreviewMutation,
  useCreateModelMutation,
  useUpdateModelMutation,
  useRevealActionSecretMutation,
  useGetModelVersionQuery,
  useGetAllModelVersionsQuery,
  useDiscardModelRevisionsMutation,
  useAddModelVersionMutation,
  useDuplicateModelMutation,
  useMigrateModelPromptMutation,
  useGetActionFlowTemplatesQuery,
  useGetNodeTemplatesQuery,
} = modelsApi;

export { modelsApi };
